// Pure string helpers, free of Tiptap, so the portal-side markdown reader and
// the importer can build the same markup the editor schema renders.

// Pinned: Lucide renames icons between majors, and a moved name renders blank.
export const LUCIDE_ICON_BASE_URL =
	"https://unpkg.com/lucide-static@1.66.0/icons"

export const DEFAULT_ICON_SIZE = 16
const MIN_ICON_SIZE = 8
const MAX_ICON_SIZE = 128

// Mintlify sites default to Font Awesome; these are the names that differ
// from Lucide's. Names both sets share pass through unchanged.
export const FONT_AWESOME_TO_LUCIDE: Record<string, string> = {
	gear: "settings",
	gears: "settings",
	"magnifying-glass": "search",
	"circle-question": "circle-help",
	question: "circle-help",
	"wand-magic-sparkles": "wand-sparkles",
	"wand-magic": "wand",
	"diagram-project": "workflow",
	comments: "messages-square",
	comment: "message-circle",
	message: "message-square",
	sitemap: "network",
	robot: "bot",
	envelope: "mail",
	"chart-line": "chart-line",
	"chart-simple": "chart-column",
	"chart-column": "chart-column",
	"chart-pie": "chart-pie",
	bolt: "zap",
	plug: "cable",
	"house-chimney": "house",
	"circle-info": "info",
	"triangle-exclamation": "triangle-alert",
	"circle-exclamation": "circle-alert",
	xmark: "x",
	"circle-xmark": "circle-x",
	"pen-to-square": "square-pen",
	trash: "trash-2",
	"trash-can": "trash-2",
	"shield-halved": "shield",
	"cart-shopping": "shopping-cart",
	bars: "menu",
	sliders: "sliders-horizontal",
	"puzzle-piece": "puzzle",
	"eye-slash": "eye-off",
	"arrow-up-right-from-square": "external-link",
	"up-right-from-square": "external-link",
	"file-lines": "file-text",
	"layer-group": "layers",
	cube: "box",
	cubes: "boxes",
	"gauge-high": "gauge",
	"user-group": "users",
	"location-dot": "map-pin",
	"paper-plane": "send",
	rotate: "refresh-cw",
	"arrows-rotate": "refresh-cw",
	spinner: "loader",
	"angle-right": "chevron-right",
	"angle-left": "chevron-left",
	"code-branch": "git-branch",
	"code-pull-request": "git-pull-request",
	"laptop-code": "laptop",
	desktop: "monitor",
	mobile: "smartphone",
	"mobile-screen": "smartphone",
	"circle-play": "circle-play",
	"book-open": "book-open",
	"life-ring": "life-buoy",
	"circle-user": "circle-user",
	building: "building-2",
}

const ICON_NAME = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const ICON_COLOR =
	/^(?:#[0-9a-fA-F]{3,8}|[a-zA-Z]{3,20}|(?:rgb|rgba|hsl|hsla)\(\s*[\d.,%\s/]+\))$/

/** The Lucide name for a Mintlify icon name, or null when it cannot be one. */
export function lucideIconName(name: unknown): string | null {
	if (typeof name !== "string") return null
	const cleaned = name
		.trim()
		.toLowerCase()
		.replace(/^fa[srlbdt]?-/, "")
	if (!ICON_NAME.test(cleaned)) return null
	return FONT_AWESOME_TO_LUCIDE[cleaned] ?? cleaned
}

export function mintlifyIconUrl(name: unknown): string | null {
	const lucide = lucideIconName(name)
	return lucide ? `${LUCIDE_ICON_BASE_URL}/${lucide}.svg` : null
}

export function clampIconSize(value: unknown): number | null {
	if (value === null || value === undefined || value === "") return null
	const parsed = Number(String(value).replace(/px$/, ""))
	if (!Number.isFinite(parsed)) return null
	return Math.min(MAX_ICON_SIZE, Math.max(MIN_ICON_SIZE, Math.round(parsed)))
}

export function sanitizeIconColor(value: unknown): string | null {
	if (typeof value !== "string") return null
	const trimmed = value.trim()
	return ICON_COLOR.test(trimmed) ? trimmed : null
}

/** The CSS custom property every masked icon reads its glyph from. */
export function iconMaskStyle(name: unknown): string | null {
	const url = mintlifyIconUrl(name)
	return url ? `--pl-icon: url("${url}")` : null
}

export const escapeHtmlAttribute = (value: string): string =>
	value
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")

export interface IconHtmlOptions {
	size?: number | string | null
	color?: string | null
}

export interface IconAttributes {
	"data-type": "icon"
	"data-icon": string
	"data-size"?: string
	"data-color"?: string
	class: string
	style: string
	"aria-hidden": "true"
}

/** The attributes of the icon element, or null for a name that is not usable. */
export function iconAttributes(
	name: unknown,
	options: IconHtmlOptions = {},
): IconAttributes | null {
	const icon = lucideIconName(name)
	const mask = iconMaskStyle(icon)
	if (!icon || !mask) return null
	const size = clampIconSize(options.size)
	const color = sanitizeIconColor(options.color)
	const style = [
		mask,
		size ? `--pl-icon-size: ${size}px` : null,
		color ? `--pl-icon-color: ${color}` : null,
	]
		.filter(Boolean)
		.join("; ")
	return {
		"data-type": "icon",
		"data-icon": icon,
		...(size ? { "data-size": String(size) } : {}),
		...(color ? { "data-color": color } : {}),
		class: "pl-icon",
		style,
		"aria-hidden": "true",
	}
}

/**
 * Mintlify's `<Icon>` as our markup: a masked span tinted with currentColor or
 * the given colour. The `<wbr>` keeps the empty span from reading as blank to
 * turndown, which would otherwise drop it before any rule runs. An unusable
 * name gives an empty string, never a broken image.
 */
export function mintlifyIconHtml(
	name: unknown,
	options: IconHtmlOptions = {},
): string {
	const attributes = iconAttributes(name, options)
	if (!attributes) return ""
	const serialized = Object.entries(attributes)
		.map(([key, value]) => `${key}="${escapeHtmlAttribute(String(value))}"`)
		.join(" ")
	return `<span ${serialized}><wbr></span>`
}

const ATTRIBUTE =
	/([\w-]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|\{\s*(?:"([^"]*)"|'([^']*)'|([^}]*))\s*\}))?/g

/**
 * Reads JSX-ish attributes. A bare name (`stroke`) reads as "true", and
 * `size={32}` or `color={"#f00"}` read as their inner value.
 */
export function readMdxAttributes(raw: string): Record<string, string> {
	const out: Record<string, string> = {}
	for (const match of raw.matchAll(ATTRIBUTE)) {
		const key = match[1]
		if (!key) continue
		const value =
			match[2] ?? match[3] ?? match[4] ?? match[5] ?? match[6]?.trim()
		out[key] = value === undefined ? "true" : decodeEntities(value)
	}
	return out
}

function decodeEntities(value: string): string {
	return value
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&amp;/g, "&")
}

// Matches attribute lists without stopping at a `>` inside a quoted value.
export const MDX_ATTRIBUTES_SOURCE = String.raw`((?:\s+[\w-]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|\{[^}]*\}))?)*)\s*`

export function writeMdxAttribute(name: string, value: string): string {
	return `${name}="${value.replace(/&/g, "&amp;").replace(/"/g, "&quot;")}"`
}
